import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import usmApi from "@/lib/usmApi";
import { PageHeader } from "../UsmLayout";
import { toast } from "sonner";
import { ArrowLeft, Printer } from "lucide-react";

const TRY = new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", minimumFractionDigits: 2 });
const STATUS = {
  balanced: { l: "Denk",  cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  over:     { l: "Fazla", cls: "text-blue-700 bg-blue-50 border-blue-200" },
  short:    { l: "Eksik", cls: "text-red-600 bg-red-50 border-red-200" },
};

export default function CashCloseDetail() {
  const { cid } = useParams();
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    usmApi.get(`/cash-close/${cid}`).then((r) => setC(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Kayıt yüklenemedi"))
      .finally(() => setLoading(false));
  }, [cid]);

  if (loading) return <div className="p-6 text-[#71717A]">Yükleniyor...</div>;
  if (!c) return <div className="p-6 text-red-600">Bulunamadı</div>;
  const st = STATUS[c.status] || { l: c.status, cls: "" };
  const b = c.breakdown?.by_type || {};

  return (
    <div data-testid="usm-cash-detail">
      <div className="print:hidden">
        <PageHeader title={`Kasa Kapanış ${c.cash_close_no}`}
          breadcrumbs={[{ label: "Panel", to: "/usm" }, { label: "Kasa Kapanış", to: "/usm/cash-close" }, { label: c.cash_close_no }]}
          actions={
            <div className="flex items-center gap-2">
              <Link to="/usm/cash-close/history" className="text-xs uppercase tracking-[0.2em] px-4 py-2.5 border border-[#E7E5E4] hover:bg-[#FAFAF9] inline-flex items-center gap-2">
                <ArrowLeft size={14} /> Geçmiş
              </Link>
              <button data-testid="cash-print-btn" onClick={() => window.print()}
                className="text-xs uppercase tracking-[0.2em] px-4 py-2.5 border border-[#E7E5E4] hover:bg-[#FAFAF9] inline-flex items-center gap-2">
                <Printer size={14} /> Yazdır
              </button>
            </div>
          } />
      </div>

      <div className="bg-white border border-[#E7E5E4] max-w-[700px] mx-auto p-6 print:border-0 print:p-0">
        <div className="text-center mb-5">
          <div className="font-heading text-2xl tracking-wider">UZUNGÖL KUYUMCULUK</div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-[#71717A] mt-0.5">KASA KAPANIŞ RAPORU</div>
        </div>

        <div className="grid grid-cols-2 gap-3 text-[12px] mb-5 pb-4 border-b border-dashed border-[#E7E5E4]">
          <Info label="Kapanış No" value={c.cash_close_no} mono />
          <Info label="İş Günü" value={c.business_day} />
          <Info label="Şube" value={c.branch?.name || "—"} />
          <Info label="Çalışan" value={c.employee?.full_name || c.employee?.username || "—"} />
          <Info label="Tarih" value={new Date(c.created_at).toLocaleString("tr-TR")} />
          <Info label="Durum"><span className={"inline-block text-[11px] uppercase tracking-[0.15em] px-2 py-1 border " + st.cls}>{st.l}</span></Info>
        </div>

        <h3 className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-2">Bugünkü Satışlar (Ödeme Tipine Göre)</h3>
        <table className="w-full text-[13px] mb-5">
          <tbody>
            <Row label="Nakit"        value={TRY.format(b.cash || 0)} />
            <Row label="Kredi Kartı"  value={TRY.format(b.card || 0)} />
            <Row label="Havale/EFT"   value={TRY.format(b.transfer || 0)} />
            <Row label="Diğer"        value={TRY.format(b.other || 0)} />
            <Row label="Toplam İşlem" value={c.breakdown?.tx_count || 0} />
            <Row label="Brüt Satış"   value={TRY.format(c.breakdown?.gross || 0)} bold />
          </tbody>
        </table>

        <h3 className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-2">Kasa Sayımı</h3>
        <table className="w-full text-[13px] mb-5">
          <tbody>
            <Row label="Açılış Kasası"   value={TRY.format(c.opening_float || 0)} />
            <Row label="Beklenen Nakit"  value={TRY.format(c.expected_cash || 0)} />
            <Row label="Sayılan Nakit"   value={TRY.format(c.actual_cash || 0)} />
            <Row label="Fark"            value={(c.difference > 0 ? "+" : "") + TRY.format(c.difference || 0)} bold cls={st.cls.split(" ")[0]} />
          </tbody>
        </table>

        {c.note && <div className="text-[11px] text-[#71717A] italic mb-4">Not: {c.note}</div>}

        <div className="text-center text-[10px] uppercase tracking-[0.25em] text-[#A8A29E] border-t border-dashed border-[#E7E5E4] pt-3">
          İmza: ______________________________
        </div>

        <style>{`@media print { @page { margin: 12mm; } aside, header, nav { display: none !important; } }`}</style>
      </div>
    </div>
  );
}

function Row({ label, value, bold, cls = "" }) {
  return (
    <tr className="border-b border-dashed border-[#F5F5F4]">
      <td className="py-1.5 text-[#71717A] text-[11px] uppercase tracking-[0.15em]">{label}</td>
      <td className={"py-1.5 text-right font-finance " + (bold ? "text-lg " : "") + cls}>{value}</td>
    </tr>
  );
}
function Info({ label, value, mono, children }) {
  return <div><div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">{label}</div><div className={mono ? "font-mono" : ""}>{children || value}</div></div>;
}
